"use client"

import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { useToast } from "@/hooks/use-toast"
import { Label } from "./ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select"

interface Contact {
  _id: string
  email: string
  name: string
  status: string
}

interface List {
  _id: string
  name: string
  description: string
  contactCount: number
  contacts: Array<{ _id: string; email: string; name: string; status: string }>
}

interface ManageContactListsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  selectedContacts: any[]
  onStatusUpdated: () => void
  statusOptionsList: Array<{ value: string; label: string }>
}


/**
 * AddStatusDialog component
 *
 * This component renders a dialog for adding a status to selected contacts.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {boolean} props.open - Whether the dialog is open or not.
 * @param {function} props.onOpenChange - The callback function for changing the open state of the dialog.
 * @param {Array<Object>} props.selectedContacts - The array of selected contacts.
 * @param {function} props.onStatusUpdated - The callback function for updating the status.
 * @returns {JSX.Element} The rendered AddStatusDialog component.
 * @author uplSouvik
 */

export function AddStatusDialog({
  open,
  onOpenChange,
  selectedContacts,
  onStatusUpdated,
  statusOptionsList,
}: ManageContactListsDialogProps) {
  const [updating, setUpdating] = useState(false)
  const [selectedStatus, setSelectedStatus] = useState("")
  const [remarks, setRemarks] = useState("")
  const { toast } = useToast()

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setSelectedStatus("")
      setRemarks("")
    }
  }, [open])

  // Handle adding status
  const handleAddStatus = async () => {
    if (selectedContacts.length === 0) {
      toast({
        title: "No contacts selected",
        description: "Please select at least one contact",
        variant: "destructive",
      })
      return
    }

    if (!selectedStatus) {
      toast({
        title: "Status required",
        description: "Please select a status",
        variant: "destructive",
      })
      return
    }

    if (!remarks.trim()) {
      toast({
        title: "Remarks required",
        description: "Please enter remarks",
        variant: "destructive",
      })
      return
    }

    try {
      setUpdating(true)

      const contactIds = selectedContacts.map(c => c.id)

      // Uncomment and update this with your actual API endpoint
      // const response = await fetch(`/api/contacts/update-status`, {
      //   method: "POST",
      //   headers: { "Content-Type": "application/json" },
      //   body: JSON.stringify({ 
      //     contactIds, 
      //     status: selectedStatus,
      //     remarks: remarks 
      //   }),
      // })

      // if (!response.ok) {
      //   throw new Error("Failed to update status")
      // }

      // For now, just log the data
      // console.log("Updating contacts:", {
      //   contactIds,
      //   status: selectedStatus,
      //   remarks: remarks
      // })

      toast({
        title: "Success",
        description: `Status updated for ${selectedContacts.length} contact(s)`,
      })

      onStatusUpdated()
      onOpenChange(false)
    } catch (error) {
      console.error(error)
      toast({
        title: "Error",
        description: "Failed to update contact status",
        variant: "destructive",
      })
    } finally {
      setUpdating(false)
    }
  }

  // Handle cancel
  const handleCancel = () => {
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Add Status</DialogTitle>
          <DialogDescription>
            {selectedContacts.length} contact(s) selected: {selectedContacts.map(c => c.email).join(", ")}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="flex-1">
            <Label htmlFor="status">Status*</Label>
            <Select
              value={selectedStatus}
              onValueChange={setSelectedStatus}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                {statusOptionsList.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1">
            <Label htmlFor="remarks">Remarks*</Label>
            <Input
              id="remarks"
              placeholder="Enter remarks"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              className="pl-2"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            disabled={updating}
            onClick={handleCancel}
            className="text-black"
          >
            Cancel
          </Button>
          <Button
            variant="outline"
            disabled={updating}
            className="bg-black text-white"
            onClick={handleAddStatus}
          >
            {updating ? "Saving..." : "Save"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}